/**
 * Floor planner — the first room of a blank one-room plan, made in five short
 * steps (Advanced mode): its name, the kind of space, its size (a common size
 * for that space, or your own numbers), the ceiling's shape, and a starting
 * finish of product on every wall and the ceiling.
 *
 * Every step has a sensible default, so the step bar along the top is a
 * shortcut as well as a map: a visitor who knows what they want can jump
 * straight to the last step and create. Nothing is committed until Create:
 * the fields are the wizard's own state. The room then opens on the blueprint
 * (FloorPlanner.createRoom), to be checked before going to 3D.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { CeilingEditor } from './CeilingEditor';
import { SpacePicker, spaceLabel } from '../chrome/SpaceMenu';
import { CeilingProfile } from '../../../calculations/ceiling/profile';
import { EMPTY_PLAN, PlanRoom, ROOM_LIMITS, StarterFinish, addRoom, setCeilingProfile } from '../../../calculations/plan';
import { defaultRoomSize } from '../../../data/roomSizePresets';
import { FinishStep, startingFinish } from './FinishStep';
import { SizePicker } from './SizePicker';
import { ROOM_STEP_ICON, RoomStep, sizeTitle } from './roomSteps';
import { DESIGNER_STRINGS as S } from '../strings';
import './createWizard.css';

const W = S.planner.wizard;

/** What the wizard hands back. */
export interface CreateRoomForm {
  name: string;
  length: number;
  width: number;
  height: number;
  ceilingProfile?: CeilingProfile;
  spaceType: string;
  /** Product on every wall and the ceiling; null for a bare room. */
  finish: StarterFinish | null;
}

type Step = RoomStep;
const STEP_ICON = ROOM_STEP_ICON;

export interface CreateRoomWizardProps {
  defaultName: string;
  spaceType: string;
  onCreate: (form: CreateRoomForm) => void;
}

export const CreateRoomWizard: React.FC<CreateRoomWizardProps> = ({ defaultName, spaceType: initialSpace, onCreate }) => {
  const [initialFinish] = useState(startingFinish);
  const steps = useMemo<Step[]>(() => (initialFinish ? ['name', 'space', 'size', 'ceiling', 'finish'] : ['name', 'space', 'size', 'ceiling']), [initialFinish]);
  const [step, setStep] = useState<Step>('name');
  const [seen, setSeen] = useState<Set<Step>>(() => new Set(['name']));
  const [name, setName] = useState(defaultName);
  const [spaceType, setSpaceType] = useState(initialSpace);
  const start = defaultRoomSize(initialSpace);
  const [length, setLength] = useState(start.length);
  const [width, setWidth] = useState(start.width);
  const [height, setHeight] = useState(start.height);
  const [custom, setCustom] = useState(false);
  const [profile, setProfile] = useState<CeilingProfile | undefined>(undefined);
  const [finish, setFinish] = useState<StarterFinish | null>(initialFinish);
  const nameTouched = useRef(false);
  const advanceTimer = useRef<number | null>(null);
  useEffect(() => () => { if (advanceTimer.current) window.clearTimeout(advanceTimer.current); }, []);

  const at = steps.indexOf(step);
  const go = (s: Step) => {
    setStep(s);
    setSeen((prev) => (prev.has(s) ? prev : new Set(prev).add(s)));
  };
  const next = () => { if (at < steps.length - 1) go(steps[at + 1]); };
  const back = () => { if (at > 0) go(steps[at - 1]); };

  const sizeTo = (p: { length: number; width: number; height: number }) => { setLength(p.length); setWidth(p.width); setHeight(p.height); };
  const pickSpace = (id: string) => {
    setSpaceType(id);
    // A new kind of space suggests its own sizes — unless the visitor typed theirs.
    if (!custom) sizeTo(defaultRoomSize(id));
    if (!nameTouched.current) setName(spaceLabel(id));
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    advanceTimer.current = window.setTimeout(() => go('size'), 220);
  };

  // The room as it would be: the ceiling step's cross-section and the finish step's walls.
  const draft = useMemo<PlanRoom | null>(() => {
    const added = addRoom(EMPTY_PLAN, { x: 0, z: 0, length, width }, height, name.trim() || defaultName);
    if (!added.ok) return null;
    const room = added.plan.rooms[0];
    if (!profile || profile.kind === 'flat') return room;
    const shaped = setCeilingProfile(added.plan, [room.id], profile);
    return shaped.ok ? shaped.plan.rooms[0] : room;
  }, [length, width, height, name, defaultName, profile]);

  const create = () => onCreate({ name: name.trim() || defaultName, length, width, height, ceilingProfile: draft?.ceilingProfile, spaceType, finish });
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (at === steps.length - 1) create(); else next();
  };

  const StepIcon = STEP_ICON[step];

  return (
    <form className="designer-toolbar cw-root" onSubmit={submit} onPointerDown={(e) => e.stopPropagation()}>
      {/* The steps: where you are, and a way to jump. */}
      <nav className="cw-stepper" aria-label={W.stepOf(at + 1, steps.length)}>
        {steps.map((s, i) => {
          const Icon = STEP_ICON[s];
          const state = s === step ? 'is-current' : seen.has(s) && i < at ? 'is-done' : '';
          return (
            <React.Fragment key={s}>
              {i > 0 && <span className={`cw-stepper-line${i <= at ? ' is-done' : ''}`} aria-hidden="true" />}
              <button type="button" className={`cw-step ${state}`} aria-current={s === step ? 'step' : undefined} onClick={() => go(s)}>
                <span className="cw-step-dot">{state === 'is-done' ? <Check size={13} /> : <Icon size={13} />}</span>
                <span className="cw-step-label">{W.steps[s]}</span>
              </button>
            </React.Fragment>
          );
        })}
      </nav>

      <div className="cw-body" key={step}>
        <div className="cw-head">
          <span className="cw-head-icon"><StepIcon size={18} /></span>
          <div>
            <h2 className="cw-title">
              {step === 'name' ? W.nameTitle
                : step === 'space' ? W.spaceTitle
                  : step === 'size' ? (custom ? W.sizeCustomTitle : sizeTitle(spaceType))
                    : step === 'ceiling' ? W.ceilingTitle : W.finishTitle}
            </h2>
            <p className="cw-intro">
              {step === 'name' ? W.nameIntro
                : step === 'space' ? W.spaceIntro
                  : step === 'size' ? (custom ? W.sizeCustomIntro : W.sizeIntro)
                    : step === 'ceiling' ? W.ceilingIntro : W.finishIntro}
            </p>
          </div>
        </div>

        {step === 'name' && (
          <input
            className="cw-name" type="text" value={name} maxLength={40} aria-label={S.planner.name} placeholder={W.namePlaceholder}
            autoFocus onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => { nameTouched.current = true; setName(e.target.value); }}
          />
        )}

        {step === 'space' && <div className="cw-space"><SpacePicker value={spaceType} onChange={pickSpace} /></div>}

        {step === 'size' && (
          <SizePicker spaceType={spaceType} size={{ length, width, height }} onChange={sizeTo} custom={custom} onCustom={setCustom} />
        )}

        {step === 'ceiling' && draft && (
          <CeilingEditor room={draft} count={1} maxHeight={ROOM_LIMITS.height.max} onChange={setProfile} variant="wizard" />
        )}

        {step === 'finish' && draft && finish && (
          <FinishStep room={draft} finish={finish} onChange={setFinish} onSkip={() => onCreate({ name: name.trim() || defaultName, length, width, height, ceilingProfile: draft.ceilingProfile, spaceType, finish: null })} />
        )}
      </div>

      <div className="cw-footer">
        {at > 0 ? (
          <button type="button" className="dh-btn" onClick={back}><ChevronLeft size={15} /> {W.back}</button>
        ) : <span />}
        <span className="cw-count">{W.stepOf(at + 1, steps.length)}</span>
        {at < steps.length - 1 ? (
          <button type="submit" className="dh-btn is-accent">{W.next} <ChevronRight size={15} /></button>
        ) : (
          <button type="submit" className="dh-btn is-accent" title={W.createHint}><Check size={15} /> {S.planner.create}</button>
        )}
      </div>
    </form>
  );
};

export default CreateRoomWizard;
